import { useQuery } from '@tanstack/react-query'
import { useTeamContext } from '@/hooks/useTeamContext'
import { useApiReady } from '@/hooks/useApiReady'
import { USER_ROLES } from '@/config/permissions'
import { missionService } from '@/services/missionService'

/**
 * Liste missions entreprise pour le bandeau / menu notifications (signatures, etc.).
 * Une seule requête partagée (clé stable) pour éviter les doubles appels lourds
 * (useNotificationItems — fetch after idle via NotificationCenter badge).
 *
 * for_notifications=1 : flux serveur « priority_pending_company » + per_page jusqu’à 500 pour inclure
 * les missions à signer entreprise même si elles ne sont pas dans les 100 dernières mises à jour.
 */
export function useCompanyNotificationMissionsQuery(options?: { enabled?: boolean }) {
  const { effectiveRole, user, activeTeamId } = useTeamContext()
  const apiReady = useApiReady()
  const isFreelancer = effectiveRole === USER_ROLES.FREELANCER
  const fetchEnabled = options?.enabled !== false

  return useQuery({
    queryKey: ['notifications', 'companyMissionsSnapshot', 'v2', user?.id, activeTeamId, effectiveRole],
    queryFn: async () => {
      if (!user?.id || !activeTeamId) return []
      const result = await missionService.getMissions({
        page: 1,
        pageSize: 500,
        // SECURITY: Send the active team so notifications never use another membership.
        teamId: activeTeamId,
        sortBy: 'priority_pending_company',
        sortDirection: 'desc',
        forNotifications: true,
      })
      if (!result?.success || !result?.data?.list) return []
      return result.data.list as Record<string, unknown>[]
    },
    enabled: apiReady && fetchEnabled && !isFreelancer && !!activeTeamId,
    staleTime: 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    retry: 0,
  })
}
